import React, { useState, useCallback } from "react";
import { Save, X } from "react-feather";
import { EditIcon } from "../../SVG/edit-icon";
import "./editable-value.scss";

export interface EditableValueProps {
  /** Value shown in view mode and used as initial value in edit mode */
  value: string;
  /** Called when save is clicked; receives the current input value */
  onSave?: (value: string) => void;
  /** Optional placeholder when input is empty */
  placeholder?: string;
  /** Optional class name for the root element */
  className?: string;
  /** Whether to show the edit button in view mode. Defaults to true. */
  showEditButton?: boolean;
}

const EditableValue = ({
  value,
  onSave,
  placeholder,
  className = "",
  showEditButton = true,
}: EditableValueProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleEdit = useCallback(() => {
    setEditValue(value);
    setIsEditing(true);
  }, [value]);

  const handleCancel = useCallback(() => {
    setEditValue(value);
    setIsEditing(false);
  }, [value]);

  const handleSave = useCallback(() => {
    onSave?.(editValue);
    setIsEditing(false);
  }, [editValue, onSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") handleSave();
      if (e.key === "Escape") handleCancel();
    },
    [handleSave, handleCancel]
  );

  if (!isEditing) {
    return (
      <div className={`editable-value ${className}`.trim()}>
        <span className="editable-value__text">{value}</span>
        {showEditButton && (
          <button
            type="button"
            onClick={handleEdit}
            className="editable-value__edit-btn"
            aria-label="Edit value"
          >
            <EditIcon color="#0056D6" size={16} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`editable-value editable-value--editing ${className}`.trim()}>
      <input
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="editable-value__input"
        autoFocus
        aria-label="Edit value"
      />
      <button
        type="button"
        onClick={handleSave}
        className="editable-value__save-btn"
        aria-label="Save"
      >
        <Save size={18} strokeWidth={2} />
      </button>
      <button
        type="button"
        onClick={handleCancel}
        className="editable-value__cancel-btn"
        aria-label="Cancel"
      >
        <X size={18} strokeWidth={2} />
      </button>
    </div>
  );
};

export { EditableValue };
export default EditableValue;
